import { Add, Delete } from '@mui/icons-material';
import { Box, Typography, Button, IconButton, Paper, Divider } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import { useSelector } from 'react-redux';

import FormSkeleton from './formSkeleton';
import FormField from 'components/common/formikForm/formField';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import { StepNavigation } from 'components/v2/StepNavigation';

import { BooleanValue } from 'constant';
import useEnabledCountries from 'hooks/useEnabledCountries';
import {
    useCountries,
    useNationalities,
    useRelationsChild,
    useRelationsNonChild,
    useFamilyMembers,
    useSaveFamilyMembers,
    useSaveChildren,
    useChildren,
} from 'hooks';
import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import type { FamilyMember, OptionInterface } from 'types';
import { generateOptionFromObject, generateOptionsFromString } from 'utils/dataFormatter';
import { emptyMember, familyMembersValidationSchema } from 'utils/validations/familyMembersSchema';

const booleanOptions = generateOptionFromObject<boolean>(BooleanValue);

interface FamilyMembersStepProps {
    isLastStep: boolean;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
}

interface FamilyMembersFormValues {
    hasAccompanyingFamilyMembers: boolean | null;
    family_members: FamilyMember[];
    hasChildren: boolean | null;
    children: FamilyMember[];
}

/**
 * Family (non-child) fields match AssessmentV2 FamilyData.
 * Child fields match AssessmentV2 ChildData (includes middlename + accompanying).
 */
const MemberListSection = ({
    isChild = false,
    title,
    hasMembers,
    members,
    relationOptions,
    countryOptions,
    nationalityOptions,
    fieldNamePrefix,
    emptyMemberTemplate,
    addButtonText,
}: {
    isChild?: boolean;
    title: string;
    hasMembers: boolean | null;
    members: FamilyMember[];
    relationOptions: OptionInterface[];
    countryOptions: OptionInterface[];
    nationalityOptions: OptionInterface[];
    fieldNamePrefix: string;
    emptyMemberTemplate: Partial<FamilyMember>;
    addButtonText: string;
}) => {
    const { hasSelfServeMarket } = useEnabledCountries();
    if (!hasMembers) return null;

    return (
        <FieldArray name={fieldNamePrefix}>
            {({ push, remove }) => (
                <Box>
                    {members.map((member, memberIndex) => (
                        <Paper key={memberIndex} elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                    {title} {memberIndex + 1}
                                </Typography>
                                {members.length > 1 && (
                                    <IconButton
                                        onClick={() => remove(memberIndex)}
                                        color="error"
                                        size="small"
                                    >
                                        <Delete />
                                    </IconButton>
                                )}
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <FormGridLayout>
                                <FormField
                                    required
                                    type="autosuggest"
                                    name={`${fieldNamePrefix}.${memberIndex}.relation`}
                                    label="relation"
                                    options={relationOptions}
                                />
                                <FormField
                                    required
                                    type="date"
                                    name={`${fieldNamePrefix}.${memberIndex}.birth_date`}
                                    label="dateOfBirth"
                                    maxDaysFromToday
                                />
                                <FormField
                                    required
                                    type="autosuggest"
                                    name={`${fieldNamePrefix}.${memberIndex}.country_of_residence`}
                                    label="countryOfResidence"
                                    options={countryOptions}
                                />
                                {!isChild && hasSelfServeMarket && (
                                    <FormField
                                        type="autosuggest"
                                        name={`${fieldNamePrefix}.${memberIndex}.country_of_birth`}
                                        label="countryOfBirth"
                                        options={countryOptions}
                                    />
                                )}
                                <FormField
                                    required
                                    type="autosuggest"
                                    name={`${fieldNamePrefix}.${memberIndex}.nationality`}
                                    label="nationality"
                                    options={nationalityOptions}
                                />
                                {isChild && (
                                    <FormField
                                        required
                                        type="radio"
                                        row
                                        name={`${fieldNamePrefix}.${memberIndex}.accompanying`}
                                        label="travellingWithYou"
                                        options={booleanOptions}
                                    />
                                )}
                                {/* Names: always shown for adults; for children only when
                                    accompanying (travelling with you) is Yes. */}
                                {(!isChild || Boolean(member.accompanying)) && (
                                    <FormField
                                        required
                                        name={`${fieldNamePrefix}.${memberIndex}.firstname`}
                                        label="firstName"
                                    />
                                )}
                                {isChild && Boolean(member.accompanying) && (
                                    <FormField
                                        name={`${fieldNamePrefix}.${memberIndex}.middlename`}
                                        label="middleName"
                                    />
                                )}
                                {(!isChild || Boolean(member.accompanying)) && (
                                    <FormField
                                        required
                                        name={`${fieldNamePrefix}.${memberIndex}.lastname`}
                                        label="lastName"
                                    />
                                )}
                            </FormGridLayout>
                        </Paper>
                    ))}

                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() =>
                            push({
                                ...emptyMemberTemplate,
                                accompanying: isChild ? false : true,
                            })
                        }
                        fullWidth
                        sx={{ mb: 3 }}
                    >
                        {addButtonText}
                    </Button>
                </Box>
            )}
        </FieldArray>
    );
};

const FamilyMembersStep = ({ onNext, onBack, isLastStep }: FamilyMembersStepProps) => {
    const { translate } = useTranslate();
    const user = useSelector((state: RootState) => state.user.entity);

    const { data: apiData, isLoading } = useFamilyMembers();
    const { data: childrenData, isLoading: childrenLoading } = useChildren();

    const { data: countries } = useCountries();
    const { data: nationalities } = useNationalities();
    const { data: relationsChild } = useRelationsChild();
    const { data: relationsNonChild } = useRelationsNonChild();

    const saveMutation = useSaveFamilyMembers();
    const saveChildrenMutation = useSaveChildren();

    const initialValues: FamilyMembersFormValues = {
        hasAccompanyingFamilyMembers: !!(apiData && apiData.length > 0),
        family_members: apiData ?? [],
        hasChildren: !!((childrenData ?? [])?.length > 0),
        children: childrenData ?? [],
    };

    const handleSubmit = async (values: FamilyMembersFormValues) => {
        try {
            const familyMembersWithUserId = (values.family_members || []).map((member) => ({
                ...member,
                user_id: user?.id || 0,
                accompanying: member.accompanying ?? true,
            }));
            const childrenWithUserId = (values.children || []).map((child) => ({
                ...child,
                user_id: user?.id || 0,
            }));

            await Promise.all([
                saveMutation.mutateAsync(
                    values.hasAccompanyingFamilyMembers ? familyMembersWithUserId : []
                ),
                saveChildrenMutation.mutateAsync(values.hasChildren ? childrenWithUserId : []),
            ]);

            onNext({
                hasAccompanyingFamilyMembers: values.hasAccompanyingFamilyMembers,
                family_members: values.hasAccompanyingFamilyMembers ? familyMembersWithUserId : [],
                hasChildren: values.hasChildren,
                children: values.hasChildren ? childrenWithUserId : [],
            });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    if (isLoading || childrenLoading) {
        return <FormSkeleton fields={8} />;
    }

    const nationalityOptions = generateOptionsFromString(nationalities ?? []);
    const countryOptions = generateOptionsFromString(countries ?? []);
    const relationChildOptions = generateOptionsFromString(relationsChild ?? []);
    const relationsNonChildOptions = generateOptionsFromString(relationsNonChild ?? []);

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={familyMembersValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, isValid, isSubmitting, setValues }) => {
                    const isSaving =
                        isSubmitting || saveMutation.isPending || saveChildrenMutation.isPending;

                    return (
                        <FormBusyProvider busy={isSaving}>
                            <Form>
                                <FormGridLayout>
                                    <FormField
                                        required
                                        type="radio"
                                        name="hasAccompanyingFamilyMembers"
                                        label="travelWithFamily"
                                        options={booleanOptions}
                                        onChange={(value: unknown) => {
                                            const boolVal =
                                                typeof value === 'boolean'
                                                    ? value
                                                    : String(value) === 'true';

                                            return setValues(
                                                {
                                                    ...values,
                                                    hasAccompanyingFamilyMembers: boolVal,
                                                    family_members: boolVal
                                                        ? [
                                                              {
                                                                  ...emptyMember,
                                                                  accompanying: true,
                                                              } as FamilyMember,
                                                          ]
                                                        : [],
                                                },
                                                true
                                            );
                                        }}
                                    />
                                </FormGridLayout>

                                {values.hasAccompanyingFamilyMembers && (
                                    <Box sx={{ mb: 4 }}>
                                        <MemberListSection
                                            title="Family Member"
                                            hasMembers={values.hasAccompanyingFamilyMembers}
                                            members={values.family_members}
                                            relationOptions={relationsNonChildOptions}
                                            countryOptions={countryOptions}
                                            nationalityOptions={nationalityOptions}
                                            fieldNamePrefix="family_members"
                                            emptyMemberTemplate={emptyMember}
                                            addButtonText={`${translate('add')} ${translate('familyMember')}`}
                                        />
                                    </Box>
                                )}

                                <Divider sx={{ my: 4 }} />

                                <FormGridLayout>
                                    <FormField
                                        type="radio"
                                        name="hasChildren"
                                        label="doYouHaveChildren"
                                        options={booleanOptions}
                                        onChange={(value: unknown) => {
                                            const boolVal =
                                                typeof value === 'boolean'
                                                    ? value
                                                    : String(value) === 'true';
                                            return setValues(
                                                {
                                                    ...values,
                                                    hasChildren: boolVal,
                                                    children: boolVal
                                                        ? [
                                                              {
                                                                  ...emptyMember,
                                                                  accompanying: false,
                                                              } as FamilyMember,
                                                          ]
                                                        : [],
                                                },
                                                true
                                            );
                                        }}
                                    />
                                </FormGridLayout>

                                {values.hasChildren && (
                                    <Box sx={{ mb: 4 }}>
                                        <MemberListSection
                                            isChild
                                            title="Child"
                                            hasMembers={values.hasChildren}
                                            members={values.children}
                                            relationOptions={relationChildOptions}
                                            countryOptions={countryOptions}
                                            nationalityOptions={nationalityOptions}
                                            fieldNamePrefix="children"
                                            emptyMemberTemplate={emptyMember}
                                            addButtonText={`${translate('add')} ${translate('child')}`}
                                        />
                                    </Box>
                                )}

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isSaving}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default FamilyMembersStep;
